const RECEIPT_LOGO = "/assets/logo/logo.png";

const loadImage = (src) =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });

const drawRoundedRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const wrapText = (ctx, text, maxWidth) => {
  const words = String(text ?? "").split(" ");
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth) {
      currentLine = candidate;
      return;
    }

    if (currentLine) {
      lines.push(currentLine);
    }
    currentLine = word;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

const downloadBlob = (blob, fileName) => {
  const downloadUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(downloadUrl);
};

export const downloadWalletReceipt = async ({
  title = "Instaport Wallet Receipt",
  subtitle = "Transaction bill",
  amountText = "",
  fields = [],
  fileName = "instaport-wallet-bill.png",
}) => {
  const rowHeight = 64;
  const detailsHeight = Math.max(fields.length, 1) * rowHeight;
  const canvasWidth = 760;
  const canvasHeight = 360 + detailsHeight + 80;
  const scale = 2;
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  canvas.width = canvasWidth * scale;
  canvas.height = canvasHeight * scale;
  context.scale(scale, scale);

  context.fillStyle = "#f6f2ea";
  context.fillRect(0, 0, canvasWidth, canvasHeight);

  context.fillStyle = "rgba(245, 158, 11, 0.10)";
  context.beginPath();
  context.arc(100, 90, 90, 0, Math.PI * 2);
  context.fill();

  context.fillStyle = "rgba(249, 115, 22, 0.08)";
  context.beginPath();
  context.arc(655, 130, 110, 0, Math.PI * 2);
  context.fill();

  context.save();
  context.shadowColor = "rgba(15, 23, 42, 0.10)";
  context.shadowBlur = 28;
  context.shadowOffsetY = 12;
  drawRoundedRect(context, 30, 24, 700, canvasHeight - 48, 28);
  context.fillStyle = "#ffffff";
  context.fill();
  context.restore();

  const logo = await loadImage(RECEIPT_LOGO);
  if (logo) {
    const desiredWidth = 150;
    const ratio = desiredWidth / logo.width;
    const desiredHeight = logo.height * ratio;
    context.drawImage(
      logo,
      (canvasWidth - desiredWidth) / 2,
      42,
      desiredWidth,
      desiredHeight
    );
  }

  context.textAlign = "center";
  context.fillStyle = "#111827";
  context.font = "700 15px Poppins, Arial, sans-serif";
  context.fillText("INSTAPORT RECEIPT", canvasWidth / 2, 128);

  context.fillStyle = "#6b7280";
  context.font = "400 11px Poppins, Arial, sans-serif";
  context.fillText(subtitle, canvasWidth / 2, 150);

  drawRoundedRect(context, 195, 175, 370, 96, 22);
  context.fillStyle = "#111827";
  context.fill();

  context.fillStyle = "#f8fafc";
  context.font = "600 11px Poppins, Arial, sans-serif";
  context.fillText(title, canvasWidth / 2, 208);
  context.font = "700 30px Poppins, Arial, sans-serif";
  context.fillText(amountText, canvasWidth / 2, 244);

  context.textAlign = "left";
  context.fillStyle = "#f59e0b";
  context.font = "700 12px Poppins, Arial, sans-serif";
  context.fillText("Transaction Details", 60, 308);

  const startY = 326;
  fields.forEach((field, index) => {
    const rowY = startY + index * rowHeight;

    drawRoundedRect(context, 52, rowY, 656, 48, 14);
    context.fillStyle = index % 2 === 0 ? "#fffaf1" : "#f8fafc";
    context.fill();

    context.fillStyle = "#6b7280";
    context.font = "600 10px Poppins, Arial, sans-serif";
    context.fillText(String(field.label || ""), 72, rowY + 18);

    context.fillStyle = "#111827";
    context.font = "600 13px Poppins, Arial, sans-serif";
    const lines = wrapText(context, field.value, 540);
    lines.slice(0, 2).forEach((line, lineIndex) => {
      context.fillText(line, 72, rowY + 35 + lineIndex * 13);
    });
  });

  context.textAlign = "center";
  context.fillStyle = "#94a3b8";
  context.font = "500 11px Poppins, Arial, sans-serif";
  context.fillText(
    "Thank you for using Instaport",
    canvasWidth / 2,
    canvasHeight - 24
  );

  const imageBlob = await new Promise((resolve) => {
    canvas.toBlob(resolve, "image/png");
  });

  if (!imageBlob) {
    throw new Error("Unable to generate wallet bill");
  }

  downloadBlob(imageBlob, fileName);
};
